import { notEmpty, type Optional } from '@festina-life/core'
import type { FieldInputProps } from 'formik'
import get from 'lodash-es/get'
import type { FocusEvent } from 'react'
import type {
  AnyObject,
  LazySchema,
  Maybe,
  ObjectSchema,
  SchemaDescription,
  SchemaFieldDescription,
  SchemaObjectDescription,
} from 'yup'

export const isSchemaDescription = (
  schemaDescription: Optional<SchemaFieldDescription>,
): schemaDescription is SchemaDescription =>
  !!schemaDescription && 'optional' in schemaDescription

export const isSchemaObjectDescription = (
  schemaDescription: Optional<SchemaFieldDescription>,
): schemaDescription is SchemaObjectDescription =>
  !!schemaDescription && 'fields' in schemaDescription

export const getOptional = (
  fieldDefinition: Optional<Record<string, SchemaFieldDescription>>,
  name: string,
) => {
  // Yup wraps the nested properties in a yup schema inside 'fields' property.
  // This supports nested yup schema structures when checking if a fields property is optional.
  // For the first level of properties this is not needed since we pass the fields
  // to the getOptional function.
  const addYupFields = name.replaceAll('.', '.fields.')
  const description = get(fieldDefinition, addYupFields)

  if (isSchemaDescription(description)) {
    return description?.optional
  }

  return true
}

export const handleOnBlur = <T>({
  event,
  field,
  onBlur,
}: {
  event: FocusEvent<HTMLElement, Element>
  field: FieldInputProps<T>
  onBlur?: (value: T) => void
}) => {
  field.onBlur(event)
  onBlur?.(field.value)
}

export const handleDefaultValueClick = <T>({
  defaultValue,
  onBlur,
  onDefaultValueClick,
}: {
  defaultValue: T
  onBlur?: (value: T) => void
  onDefaultValueClick?: () => void
}) => {
  if (notEmpty(defaultValue)) {
    onDefaultValueClick?.()
    onBlur?.(defaultValue)
  }
}

export const handleExistingValueClick = <T>({
  existingValue,
  setValue,
  onBlur,
}: {
  existingValue: T
  setValue: (value: T) => void
  onBlur?: (value: T) => void
}) => {
  if (notEmpty(existingValue)) {
    setValue(existingValue)
    onBlur?.(existingValue)
  }
}

export const isYupObjectSchema = (
  schema:
    | Optional<ObjectSchema<Maybe<AnyObject>>>
    | Optional<ObjectSchema<Maybe<AnyObject>, null, Maybe<AnyObject>>>
    | LazySchema<unknown>,
): schema is ObjectSchema<Maybe<AnyObject>> => schema?.type === 'object'
